import { useState, useRef, useCallback, useEffect } from 'react';
import { d as disableScroll, e as enableScroll } from './body-5de40423.js';

/**
Dialog Hooks, like the file name says
Handles management of open/close state
Adding/removing CSS classes to body/html
*/

function useAnimatedModalState(params = {}) {
  const {
    status: initialStatus,
    onToggle
  } = params;
  const [status, setStatus] = useState(initialStatus || 'initial');
  const bodyElem = useRef();
  const htmlElem = useRef();
  const componentRef = useRef();
  const afterOpen = useCallback(() => {
    if (componentRef.current) {
      componentRef.current.removeEventListener('transitionend', afterOpen);
    }
    setStatus('open');
  }, [componentRef, setStatus]);
  const afterClose = useCallback(() => {
    if (componentRef.current) {
      componentRef.current.removeEventListener('transitionend', afterClose);
    }
    setStatus('closed');
  }, [componentRef, setStatus]);
  const clearListeners = useCallback(() => {
    if (componentRef.current) {
      componentRef.current.removeEventListener('transitionend', afterOpen);
      componentRef.current.removeEventListener('transitionend', afterClose);
    }
  }, [componentRef, afterOpen, afterClose]);
  const addBodyClasses = useCallback(() => {
    if (bodyElem && bodyElem.current) {
      disableScroll(bodyElem.current);
    }
    if (htmlElem && htmlElem.current) {
      disableScroll(htmlElem.current);
    }
  }, [bodyElem, htmlElem]);
  const removeBodyClasses = useCallback(() => {
    if (bodyElem && bodyElem.current) {
      enableScroll(bodyElem.current);
    }
    if (htmlElem && htmlElem.current) {
      enableScroll(htmlElem.current);
    }
  }, [bodyElem, htmlElem]);
  const animateOpen = useCallback(() => {
    if (componentRef.current) {
      componentRef.current.addEventListener('transitionend', afterOpen);
    }
    setStatus('opening');
  }, [componentRef, afterOpen]);
  const openModal = useCallback(() => {
    if (status === 'open') return;
    clearListeners();
    addBodyClasses();
    setStatus('beforeopen');
    setTimeout(animateOpen, 100);
    if (typeof onToggle === 'function') onToggle(true);
  }, [status, clearListeners, addBodyClasses, animateOpen, onToggle]);
  const closeModal = useCallback(() => {
    if (status === 'closed') return;
    removeBodyClasses();
    setStatus('closing');
    if (componentRef.current) {
      componentRef.current.addEventListener('transitionend', afterClose);
    }
    if (typeof onToggle === 'function') onToggle(true);
  }, [status, removeBodyClasses, afterClose, onToggle]);
  useEffect(() => {
    bodyElem.current = document.body;
    htmlElem.current = document.documentElement;
    return function cleanup() {
      clearListeners();
      removeBodyClasses();
    };
  }, [clearListeners, removeBodyClasses]);
  return {
    open: !['initial', 'closed'].includes(status),
    status,
    componentRef,
    openModal,
    closeModal
  };
}

export { useAnimatedModalState as default };
